import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type { WaageExerciseConfig, WaageRunSettings } from "@calega/content";
import type { WaageRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
type WaageChoice = "left" | "equal" | "right";

interface WaageAnswer {
  itemId: string;
  answer: WaageChoice;
  correct: boolean;
}

interface WaageState {
  runId: string;
  run: WaageRun;
  index: number;
  phase: Phase;
  endsAt: number | null;
  answers: WaageAnswer[];
  result: { score: number; maxScore: number } | null;
}

const ranges: Array<{ id: WaageRunSettings["range"]; label: string }> = [
  { id: "1-5", label: "1–5" },
  { id: "1-10", label: "1–10" },
  { id: "1-20", label: "1–20" },
];

const difficulties: Array<{ id: WaageRunSettings["difficulty"]; label: string; description: string }> = [
  { id: "leicht", label: "Leicht", description: "Geordnet und gleich groß" },
  { id: "mittel", label: "Mittel", description: "Starke Größenkontraste und verschiedene Motive" },
  { id: "schwer", label: "Schwer", description: "Gestreut, unterschiedlich groß und verschiedene Motive" },
];

const choices: Array<{ id: WaageChoice; label: string }> = [
  { id: "left", label: "◀ Links mehr" },
  { id: "equal", label: "= Gleich" },
  { id: "right", label: "Rechts mehr ▶" },
];

function solutionText(left: number, right: number, correct: WaageChoice) {
  if (correct === "equal") return `Beide Seiten haben gleich viele Objekte (${left}).`;
  const side = correct === "left" ? "Links" : "Rechts";
  return `${side} hat mehr (${left} gegenüber ${right}).`;
}

function waageGridFontSize(count: number, scale: number) {
  const base = count <= 5 ? 1.8 : count <= 10 ? 1.25 : count <= 15 ? 1.05 : 0.9;
  return `${Math.max(0.58, base * scale).toFixed(2)}rem`;
}

function WaageObjects({ emoji, side }: { emoji: string; side: WaageRun["items"][number]["presentation"]["left"] }) {
  const canvasStyle = side.canvas ? {
    maxWidth: `${side.canvas.width}px`,
    aspectRatio: `${side.canvas.width} / ${side.canvas.height}`,
  } : undefined;
  return <div className={`waage-items ${side.canvas ? "is-scattered" : ""}`} style={canvasStyle} aria-hidden="true">
    {side.objects.map((object, index) => (
      <span
        className="waage-object"
        key={index}
        style={{
          left: `${object.x}%`,
          top: `${object.y}%`,
          fontSize: object.fontSize ? `${object.fontSize}cqh` : waageGridFontSize(side.objects.length, object.scale),
          transform: `translate(-50%, -50%) rotate(${object.rotation}deg)`,
        }}
      >
        {emoji}
      </span>
    ))}
  </div>;
}

export function Waage({ account, config, pageId }: { account: Account; config: WaageExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<WaageRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [range, setRange] = useState<WaageRunSettings["range"]>(config.defaultRange);
  const [difficulty, setDifficulty] = useState<WaageRunSettings["difficulty"]>(config.defaultDifficulty);
  const [exercise, setExercise] = useState<WaageState | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  const score = useMemo(() => exercise?.answers.filter((answer) => answer.correct).length ?? 0, [exercise?.answers]);

  function publish(state: WaageState | null) {
    realtime.publishExercise({ exerciseType: "waage", pageId, state });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: WaageState | null } | null;
    if (synced?.exerciseType === "waage" && synced.pageId === pageId) setExercise(synced.state ?? null);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => tick((current) => current + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timeout = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  function settings(): WaageRunSettings {
    return { mode, count, durationSeconds, range, difficulty };
  }

  async function start(runSettings = settings()) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: WaageRun }>({ contentPageId: pageId, settings: runSettings });
      const next: WaageState = {
        runId: response.runId,
        run: response.exercise,
        index: 0,
        phase: "running",
        endsAt: response.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000 : null,
        answers: [],
        result: null,
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setRange(exercise.run.settings.range);
      setDifficulty(exercise.run.settings.difficulty);
      setExercise(null);
      setMessage("");
      publish(null);
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try {
      await abort();
    } finally {
      setBusy(false);
    }
    await start(runSettings);
  }

  async function answer(answer: WaageChoice) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "waage", itemId: item.id, answer });
      const next: WaageState = {
        ...exercise,
        phase: "feedback",
        answers: [...exercise.answers, { itemId: item.id, answer, correct: answer === item.correct }],
      };
      setExercise(next);
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next: WaageState = { ...exercise, result, phase: "complete" };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) {
      await finish();
      return;
    }
    const nextState: WaageState = { ...exercise, index: exercise.index + 1, phase: "running" };
    setExercise(nextState);
    publish(nextState);
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) {
    return <ExerciseWaitingCard className="waage-exercise" />;
  }

  if (!exercise) {
    return <section className="waage-exercise">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="mathe-settings waage-settings">
        <fieldset>
          <legend>Zahlenraum</legend>
          <div className="mathe-option-grid">
            {ranges.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-waage-range`} checked={range === item.id} onChange={() => setRange(item.id)} />{item.label}</label>)}
          </div>
        </fieldset>
        <fieldset>
          <legend>Schwierigkeitsgrad</legend>
          <div className="waage-difficulty-options">
            {difficulties.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-waage-difficulty`} checked={difficulty === item.id} onChange={() => setDifficulty(item.id)} /><span><strong>{item.label}</strong><small>{item.description}</small></span></label>)}
          </div>
        </fieldset>
        <fieldset>
          <legend>Durchlauf</legend>
          <div className="mathe-run-settings">
            <div className="blitz-choice-row">
              <label className="checkbox-label"><input type="radio" name={`${pageId}-waage-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label>
              <label className="checkbox-label"><input type="radio" name={`${pageId}-waage-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label>
            </div>
            {mode === "count"
              ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value) as WaageRunSettings["count"])}>{[5, 10, 15, 20].map((value) => <option value={value} key={value}>{value}</option>)}</select></label>
              : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}
          </div>
        </fieldset>
      </div>
      <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Waagenaufgaben starten</button>
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const item = exercise.run.items[exercise.index];
  const leftEmoji = item?.emojis?.left ?? item?.emoji ?? "";
  const rightEmoji = item?.emojis?.right ?? item?.emoji ?? "";
  const lastAnswer = exercise.answers.at(-1);
  const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const completed = exercise.answers.length;

  return <section className="waage-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${completed} beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <div className="waage-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? completed} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div> : item && <>
      <div className={`waage-card ${exercise.phase === "feedback" ? `resolved-${item.correct}` : ""}`}>
        <div className="waage-scene" aria-label={`Links sind ${item.left} ${leftEmoji}, rechts sind ${item.right} ${rightEmoji}`}>
          <div className="waage-base" aria-hidden="true"><span /></div>
          <div className="waage-beam">
            <div className={`waage-pan waage-pan-left ${exercise.phase === "feedback" && item.correct === "left" ? "is-heavier" : exercise.phase === "feedback" && item.correct === "equal" ? "is-equal" : ""}`}>
              <WaageObjects emoji={leftEmoji} side={item.presentation.left} />
              <span className="waage-pan-label">Links</span>
            </div>
            <div className="waage-pivot" aria-hidden="true" />
            <div className={`waage-pan waage-pan-right ${exercise.phase === "feedback" && item.correct === "right" ? "is-heavier" : exercise.phase === "feedback" && item.correct === "equal" ? "is-equal" : ""}`}>
              <WaageObjects emoji={rightEmoji} side={item.presentation.right} />
              <span className="waage-pan-label">Rechts</span>
            </div>
          </div>
        </div>
        <h2 className="learning-text">Welche Seite hat mehr?</h2>
        <p className="waage-count-hint">Zähle nur die Objekte – Größe und Symbol spielen keine Rolle.</p>
      </div>
      {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig. " : "Nicht ganz. "}{solutionText(item.left, item.right, item.correct)}</div>}
      <div className="waage-actions">
        {exercise.phase === "running" ? choices.map((choice) => <button className="secondary waage-choice" disabled={busy || !canAnswer} key={choice.id} onClick={() => answer(choice.id).catch((error) => setMessage(error.message))}>{choice.label}</button>) : canControl
          ? <button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow) || exercise.index + 1 >= exercise.run.items.length ? "Auswertung ansehen" : "Nächste Aufgabe"}</button>
          : <p className="muted">Die nächste Aufgabe wird von der Sitzungsleitung geöffnet.</p>}
      </div>
    </>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
